import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, MessageCircle, Phone, MapPin } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { Seo } from "@/components/site/Seo";
import { CITIES, cityPath, cityPhoto } from "@/data/cities";
import { FINISHES } from "@/data/finishes";
import { cityEnquiry, CALL_URL, PHONE_DISPLAY } from "@/data/company";

const steps = (c) => [
  ["Share your requirement", `Finish, size, approximate square feet and your site location in ${c.name}.`],
  ["Get photos & a quote", `Actual stock photos, current rates and freight to ${c.name} — on WhatsApp.`],
  ["Inspected, packed, dispatched", `Every lot is checked and packed at our Kota units before it leaves for ${c.name}.`],
];

const faqs = (c) => [
  [`Do you deliver Kota stone to ${c.name}?`, `Yes. We dispatch from our Kota units to project sites in ${c.name} and across ${c.state}. Freight is quoted for your exact site location along with the stone.`],
  [`What is the price of Kota stone in ${c.name}?`, `It depends on the finish, size, thickness and quantity, plus freight to ${c.name}. Send your requirement on WhatsApp and we'll share current rates.`],
  ["Can I see the stone before ordering?", "Yes — we share real photos and videos of the stock on WhatsApp, and you're welcome to visit our yards in Kota by appointment."],
];

const CityHero = ({ city }) => (
  <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
    <div className="lg:col-span-7 min-w-0">
      <p className="font-heading text-xs uppercase tracking-[0.25em] text-copper mb-4" data-testid="city-eyebrow">Kota stone supplier · {city.state}</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold leading-[1.05]" data-testid="city-title">
        Kota Stone in <span className="font-editorial italic font-medium text-copper">{city.name}</span>
      </h1>
      <p className="text-sm md:text-base text-bone/65 leading-relaxed max-w-2xl mt-6" data-testid="city-intro">
        Buy Kota stone in {city.name}{city.alias ? ` (${city.alias})` : ""} direct from the manufacturer. Garg Stone Company has mined and finished
        natural stone in Kota, Rajasthan since 1988 — Kota Blue, Kota Yellow, mirror-polish, leather and shot-blast flooring, cobbles,
        paving and wall cladding — supplied to homes, builders and architects across {city.state}.
      </p>
      <div className="flex flex-wrap items-center gap-5 mt-9">
        <a href={cityEnquiry(city)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-copper text-ink px-6 py-4 text-sm font-heading font-bold transition-colors hover:bg-bone" data-testid="city-whatsapp-button"><MessageCircle size={18} className="shrink-0" /> Get a quote for {city.name} <ArrowUpRight size={16} /></a>
        <a href={CALL_URL} className="inline-flex gap-2 items-center text-sm text-bone/70 transition-colors hover:text-copper" data-testid="city-call-button"><Phone size={15} /> {PHONE_DISPLAY}</a>
      </div>
    </div>
    <Reveal delay={0.1} className="lg:col-span-5 min-w-0">
      <div className="aspect-[4/3] bg-stone overflow-hidden"><img src={cityPhoto(city)} alt="Kota stone flooring by Garg Stone Company" className="h-full w-full object-cover" data-testid="city-image" /></div>
    </Reveal>
  </div>
);

const CitySteps = ({ city }) => (
  <section className="mt-24" data-testid="city-steps">
    <Reveal><h2 className="font-heading text-lg mb-8">Ordering Kota stone from {city.name}</h2></Reveal>
    <ol className="grid md:grid-cols-3 gap-5">
      {steps(city).map(([title, text], i) => (
        <Reveal key={title} delay={i * 0.08}>
          <li className="h-full border border-bone/15 p-7" data-testid={`city-step-${i}`}>
            <span className="font-editorial italic text-2xl text-copper">0{i + 1}</span>
            <p className="mt-4 font-heading font-bold">{title}</p>
            <p className="mt-2 text-sm font-light text-bone/60 leading-relaxed">{text}</p>
          </li>
        </Reveal>
      ))}
    </ol>
  </section>
);

const CityFinishes = ({ city }) => (
  <section className="mt-24" data-testid="city-finishes">
    <Reveal><h2 className="font-heading text-lg mb-8">Finishes we supply to {city.name}</h2></Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 border-t border-bone/15">
      {FINISHES.map((f) => (
        <Link key={f.id} to={`/finishes/${f.id}`} className="group flex justify-between items-center gap-4 py-4 pr-4 border-b border-bone/15 transition-colors hover:text-copper" data-testid={`city-finish-${f.id}`}>
          <span><span className="block font-heading text-sm font-bold">{f.name}</span><span className="block text-xs text-bone/45 mt-0.5">{f.finish}</span></span>
          <ArrowUpRight size={15} className="shrink-0 text-bone/40 transition-transform group-hover:translate-x-0.5" />
        </Link>
      ))}
    </div>
  </section>
);

const CityAreas = ({ city }) => (
  <Reveal>
    <h2 className="font-heading text-lg mb-6">Delivering in and around {city.name}</h2>
    <ul className="flex flex-wrap gap-3" data-testid="city-areas">
      {city.areas.map((a) => <li key={a} className="inline-flex items-center gap-2 border border-bone/15 px-4 py-2 text-sm text-bone/70"><MapPin size={13} className="text-copper" />{a}</li>)}
    </ul>
  </Reveal>
);

const CityFaq = ({ city }) => (
  <Reveal delay={0.1}>
    <h2 className="font-heading text-lg mb-6">Questions from {city.name} buyers</h2>
    <dl className="flex flex-col gap-6" data-testid="city-faq">
      {faqs(city).map(([q, a]) => <div key={q} className="border-b border-bone/15 pb-6"><dt className="font-heading font-bold">{q}</dt><dd className="mt-2 text-sm font-light text-bone/60 leading-relaxed">{a}</dd></div>)}
    </dl>
  </Reveal>
);

const OtherCities = ({ city }) => (
  <section className="mt-24 border-t border-bone/15 pt-9" data-testid="city-other-cities">
    <h2 className="text-lg font-heading mb-5">Kota stone in other cities</h2>
    <div className="flex flex-wrap gap-x-6 gap-y-3">
      {CITIES.filter((c) => c !== city).map((c) => <Link key={c.slug} to={cityPath(c)} className="text-sm text-bone/60 transition-colors hover:text-copper" data-testid={`city-link-${c.slug}`}>Kota Stone in {c.name}</Link>)}
    </div>
  </section>
);

export default function CityPage({ city }) {
  return (
    <article key={city.slug} id="top" className="pt-28 md:pt-36 pb-24 px-6 md:px-12 lg:px-20" data-testid="city-page">
      <Seo
        title={`Kota Stone in ${city.name} — Factory-Direct Supplier | Garg Stone Company`}
        description={`Buy Kota stone in ${city.name}, ${city.state} direct from the manufacturer in Kota (since 1988). Kota Blue, Kota Yellow, polished & leather flooring, cobbles, cladding. WhatsApp for price & freight.`}
        path={cityPath(city)}
      />
      <Link to="/#india" className="inline-flex gap-2 items-center mb-10 text-xs uppercase font-heading text-bone/60 transition-colors hover:text-copper" data-testid="city-back-link"><ArrowLeft size={15} /> Kota stone across India</Link>
      <CityHero city={city} />
      <CitySteps city={city} />
      <CityFinishes city={city} />
      <div className="mt-24 grid lg:grid-cols-2 gap-14"><CityAreas city={city} /><CityFaq city={city} /></div>
      <OtherCities city={city} />
    </article>
  );
}
